import { useEffect, useState } from 'react';
import { api, ago, humanise, type KycItem } from '../api';
import { PageHead, Panel, useLoad } from '../ui';

/**
 * Driver-facing rejection copy, mirrored from packages/shared/verification.ts.
 *
 * Shown to the REVIEWER before they confirm, because a rejection is a message
 * to a person who has just spent twenty minutes photographing documents, and
 * the reviewer should see exactly what that person will read. Duplicated here
 * rather than fetched: it is not worth an endpoint, and the API is the
 * authority on what actually gets sent.
 */
const REJECTION: Record<string, { message: string; action: string }> = {
  RC_CLASS_NOT_COMMERCIAL: {
    message: 'Registration shows a private vehicle class. Carrying paying passengers needs a commercial registration with a yellow plate.',
    action: 'Register the vehicle commercially, or add a different vehicle.',
  },
  DL_NO_TRANSPORT_BADGE: {
    message: 'Licence has no transport endorsement, which is required to carry passengers for hire.',
    action: 'Apply for a transport endorsement at your RTO, then re-submit.',
  },
  DL_EXPIRED: { message: 'Driving licence has expired.', action: 'Renew it and upload the new licence.' },
  RC_EXPIRED: { message: 'Vehicle registration has expired.', action: 'Renew the RC and re-submit.' },
  INSURANCE_EXPIRED: { message: 'Insurance policy has expired.', action: 'Upload the renewed policy.' },
  PERMIT_MISSING: { message: 'No valid passenger permit found for this vehicle.', action: 'Upload the contract carriage or state permit.' },
  FITNESS_EXPIRED: { message: 'Fitness certificate has expired.', action: 'Renew it at the RTO and re-submit.' },
  NAME_MISMATCH: { message: 'Name does not match the driver’s other documents.', action: 'Check the profile spelling, or upload a document with the current legal name.' },
  RC_OWNER_MISMATCH: { message: 'Vehicle is registered to someone else.', action: 'Upload an RC in your own name, or a notarised authorisation.' },
  DOCUMENT_ILLEGIBLE: { message: 'The photo is too blurred or dark to read.', action: 'Retake it in daylight, flat, with the whole document in frame.' },
  SELFIE_MISMATCH: { message: 'Selfie does not match the photo ID.', action: 'Retake the selfie in good light, facing the camera.' },
  DUPLICATE_DOCUMENT: { message: 'This document is already registered to another driver.', action: 'Contact support if you believe this is wrong.' },
};

export function Kyc() {
  const [status, setStatus] = useState('in_review');
  const { data, error, loading, reload } = useLoad(() => api.kyc(status), { deps: [status] });
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [zoom, setZoom] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const queue = data ?? [];
  const item: KycItem | undefined = queue[Math.min(cursor, Math.max(queue.length - 1, 0))];

  // A fresh item means a fresh decision. Carrying a rejection reason across
  // two drivers is exactly the mistake this clears.
  useEffect(() => {
    setNote('');
    setCode('');
    setZoom(false);
  }, [item?.id]);

  const decide = async (decision: 'approve' | 'reject') => {
    if (!item) return;
    if (decision === 'reject' && !code) {
      setFlash('Pick a reason — the driver is told how to fix it.');
      return;
    }
    setBusy(true);
    try {
      await api.review(item.id, {
        decision,
        ...(decision === 'reject' ? { rejectionCode: code } : {}),
        ...(note.trim() ? { notes: note.trim() } : {}),
      });
      setFlash(`${decision === 'approve' ? 'Approved' : 'Rejected'} · ${item.fullName ?? 'driver'}`);
      // Stay at the same index: the reviewed item leaves the queue, so the
      // next one slides into place and the reviewer keeps their rhythm.
      await reload();
    } catch (e) {
      setFlash(e instanceof Error ? e.message : 'Could not save that decision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        title="Document review"
        blurb="Read the document, then decide. A rejection must carry a reason — it becomes the message the driver reads, so it has to tell them what to fix."
      />

      <Panel
        title={`${queue.length} waiting`}
        right={
          <>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setCursor(0); }}>
              <option value="in_review">In review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
            <button className="btn sm" onClick={() => void reload()} disabled={loading}>
              Refresh
            </button>
          </>
        }
      >
        {error ? <div className="empty">{error}</div> : null}

        {!error && loading && !data ? <div className="empty">Loading…</div> : null}

        {!error && data && queue.length === 0 ? (
          <div className="empty">
            {status === 'in_review'
              ? 'Nothing waiting. Every submitted document has been decided.'
              : `No ${status.replace('_', ' ')} documents.`}
          </div>
        ) : null}

        {item ? (
          <div className="review">
            <div className="docs">
              {item.documents.length === 0 ? (
                <p className="none">
                  No document attached — this method verifies without an upload.
                </p>
              ) : (
                item.documents.map((d) => (
                  <Doc
                    key={d.objectKey}
                    url={d.url}
                    alt={`${humanise(item.requirement)} submitted by ${item.fullName ?? 'driver'}`}
                    zoom={zoom}
                    onToggleZoom={() => setZoom((z) => !z)}
                  />
                ))
              )}
            </div>

            <div className="decide">
              <h3>{humanise(item.requirement)}</h3>
              <p className="who">
                {item.fullName ?? 'Unnamed driver'} · {item.phone ?? 'no phone'}
              </p>

              {item.plateWarning ? (
                <div className="plate-warn">
                  <strong>White number plate.</strong> A privately registered vehicle cannot
                  legally carry paying passengers, whatever this document says.
                </div>
              ) : null}

              <dl className="facts">
                <div>
                  <dt>Method</dt>
                  <dd>{humanise(item.method)}</dd>
                </div>
                {item.numberLast4 ? (
                  <div>
                    <dt>Number</dt>
                    <dd className="mono">••••{item.numberLast4}</dd>
                  </div>
                ) : null}
                {item.vehicle ? (
                  <>
                    <div>
                      <dt>Vehicle</dt>
                      <dd className="mono">{item.vehicle.registrationNo}</dd>
                    </div>
                    <div>
                      <dt>Plate</dt>
                      <dd>{item.vehicle.plateColour ?? 'not stated'}</dd>
                    </div>
                    {item.vehicle.rcClass ? (
                      <div>
                        <dt>RC class</dt>
                        <dd>{item.vehicle.rcClass}</dd>
                      </div>
                    ) : null}
                  </>
                ) : null}
                <div>
                  <dt>Submitted</dt>
                  <dd>{ago(item.createdAt)}</dd>
                </div>
              </dl>

              {status === 'in_review' ? (
                <>
                  <label className="field">
                    <span>Reason (required to reject)</span>
                    <select value={code} onChange={(e) => setCode(e.target.value)}>
                      <option value="">—</option>
                      {Object.keys(REJECTION).map((k) => (
                        <option key={k} value={k}>{humanise(k)}</option>
                      ))}
                    </select>
                  </label>

                  {code ? (
                    <div className="reject-copy">
                      <strong>The driver will read:</strong>
                      {REJECTION[code]?.message}
                      <br />
                      <em>{REJECTION[code]?.action}</em>
                    </div>
                  ) : null}

                  <label className="field">
                    <span>Internal note (optional)</span>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Anything the next reviewer should know"
                    />
                  </label>

                  <div className="actions">
                    <button
                      className="btn go"
                      disabled={busy}
                      onClick={() => void decide('approve')}
                    >
                      Approve
                    </button>
                    <button
                      className="btn stop"
                      disabled={busy || !code}
                      onClick={() => void decide('reject')}
                    >
                      Reject
                    </button>
                  </div>
                </>
              ) : (
                <p className="who">Already {humanise(item.status)}. Nothing to decide.</p>
              )}

              {queue.length > 1 ? (
                <div className="actions" style={{ marginTop: 14 }}>
                  <button
                    className="btn sm"
                    disabled={cursor === 0}
                    onClick={() => setCursor((c) => Math.max(0, c - 1))}
                  >
                    Previous
                  </button>
                  <button
                    className="btn sm"
                    disabled={cursor >= queue.length - 1}
                    onClick={() => setCursor((c) => Math.min(queue.length - 1, c + 1))}
                  >
                    Skip
                  </button>
                  <span className="who num" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
                    {Math.min(cursor + 1, queue.length)} / {queue.length}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Panel>

      {flash ? <FlashToast msg={flash} onDone={() => setFlash(null)} /> : null}
    </>
  );
}

function FlashToast({ msg, onDone }: { msg: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [msg, onDone]);
  return <div className="toast">{msg}</div>;
}

/**
 * One document, with an honest failure state.
 *
 * A broken image icon tells the reviewer nothing, and the two reasons it
 * happens need different responses: an expired read link is fixed by
 * refreshing, a missing object never will be and the driver has to re-upload.
 * Leaving them to guess is how a reviewer ends up rejecting someone for a
 * storage problem.
 */
function Doc({
  url, alt, zoom, onToggleZoom,
}: { url: string | null; alt: string; zoom: boolean; onToggleZoom: () => void }) {
  const [failed, setFailed] = useState(false);

  if (!url) {
    return (
      <p className="none">
        No read link for this file. The storage service may be unreachable.
      </p>
    );
  }
  if (failed) {
    return (
      <p className="none">
        This file did not load. Either the five-minute read link has expired —
        refresh the queue — or the upload never completed, in which case ask the
        driver to submit it again rather than rejecting the document.
      </p>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className={zoom ? 'zoom' : undefined}
      onClick={onToggleZoom}
      onError={() => setFailed(true)}
    />
  );
}
